import { useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import {
  useSubscriptionPlans,
  useCreatePlan,
  useUpdatePlan,
  useDeletePlan
} from '../../hooks/usePlans.js';
import { PlanCard } from './PlanCard.jsx';
import { PlanForm } from './PlanForm.jsx';
import { ConfirmModal } from '../ConfirmModal.jsx';

/**
 * إدارة باقات الاشتراك الشهري.
 * تُستعمل في شاشة الاشتراكات وفي تبويب داخل شاشة إعدادات التسعير (نفس المكوّن
 * في المكانين، فلا تتفرّع الواجهة).
 */
export function PlansList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canEdit = hasPermission('pricing.edit');
  const { data: plans = [], isLoading, error } = useSubscriptionPlans();
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  function openCreate() { setEditing(null); setFormOpen(true); }
  function openEdit(plan) { setEditing(plan); setFormOpen(true); }
  function closeForm() { setFormOpen(false); setEditing(null); }

  async function handleSubmit(body) {
    if (editing) {
      await updatePlan.mutateAsync(
        { id: editing._id || editing.id, body },
        { onSuccess: () => closeForm() }
      );
    } else {
      await createPlan.mutateAsync(body, { onSuccess: () => closeForm() });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[15px] font-bold">باقات الاشتراك الشهري</div>
          <div className="text-xs text-ink-3 mt-0.5">اسم، سعر، ومدة بالأيام — دخول غير محدود خلال الفترة</div>
        </div>
        {canEdit ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> باقة اشتراك جديدة
          </button>
        ) : null}
      </div>

      {error ? (
        <Card><div className="text-red text-center py-4">تعذّر تحميل باقات الاشتراك</div></Card>
      ) : isLoading ? (
        <Card><div className="text-ink-3 text-center py-8">جاري التحميل...</div></Card>
      ) : plans.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 flex flex-col items-center gap-3">
            <Icon name="card" size={42} color="#D6CCB3" />
            <div className="text-[14px] font-semibold text-ink-2">
              لا توجد باقات اشتراك شهري — أنشئ الأولى
            </div>
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                <Icon name="plus" size={14} /> باقة اشتراك جديدة
              </button>
            ) : null}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {plans.map((p) => (
            <PlanCard
              key={p._id || p.id}
              plan={p}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={(plan) => setConfirmDelete(plan)}
            />
          ))}
        </div>
      )}

      <PlanForm
        open={formOpen}
        plan={editing}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={createPlan.isPending || updatePlan.isPending}
      />

      <ConfirmModal
        open={!!confirmDelete}
        title="تعطيل باقة الاشتراك"
        message={`سيتم تعطيل "${confirmDelete?.name || ''}" فلا تظهر عند تسجيل اشتراك جديد. الاشتراكات المبيعة سابقاً تبقى كما هي.`}
        confirmLabel="تعطيل"
        variant="danger"
        loading={deletePlan.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deletePlan.mutateAsync(
            confirmDelete._id || confirmDelete.id,
            { onSuccess: () => setConfirmDelete(null) }
          );
        }}
      />
    </div>
  );
}
